import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { UserState } from '@state/user.state';
import { buildCountryCitiesLUT, buildCountryList, City, Country } from 'app/@types/shared/world-cities';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { IndustryService } from '@services/common/industry.service';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { TranslatePluralPipe } from 'app/main/pipes/translate-plural.pipe';
import { ToastService } from '@services/common/toast.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationState } from '@state/organization.state';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UtilsService } from '@services/common/utils.service';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';

interface Industry extends IndustryDto {
    label: string;
}

@Component({
    selector: 'app-settings-form-entities',
    templateUrl: './settings-form-entities.component.html',
    styleUrls: ['./settings-form-entities.component.scss'],
})
export class SettingsFormEntitiesComponent implements OnInit, OnDestroy {
    @Select(OrganizationState.getOrganization) organization$: Observable<OrganizationDto>;
    @Select(OrganizationState.getEntities) entities$: Observable<OrganizationEntityDto[]>;
    @Select(QuestionnaireState.getQuestionnaire) activeQuestionnaires$: Observable<QuestionnaireDto>;
    @Select(OrganizationState.getOrganizationPricingPlan) pricingPlan$: Observable<OrganizationPricingPlanDto>;
    @Select(UserState.getCurrentUserClaims) userClaims$: Observable<UserClaims>;

    public organization: OrganizationDto;
    public entities: OrganizationEntityDto[] = [];
    public selectedEntity: OrganizationEntityDto | null = null;
    public addingEditingMode: boolean = false;
    private ngUnsubscribe = new Subject<void>();
    public activeQuestionnaire: QuestionnaireDto;
    public pricingPlan: OrganizationPricingPlanDto;
    public isLoading: boolean = true;
    public isOrgOwner: boolean = false;
    public canAddEntities: boolean = false;
    public currencies: CurrencyDto[] = [];

    private countries: Country[];
    private countryCitiesLUT: Map<string, City[]>;
    public industries: IndustryDto[] = [];
    private industries$ = this.industryService.get();

    private industryTranslations;

    constructor(
        private industryService: IndustryService,
        private translateService: CustomTranslateService,
        public toastService: ToastService,
        private ngZone: NgZone,
        private questionnaireService: QuestionnaireHttpService,
    ) {}

    ngOnInit() {
        Chathive.widget.setContextField({ key: 'currentPage', value: 'Entities' });
        combineLatest([
            this.organization$,
            this.industries$,
            this.entities$,
            this.activeQuestionnaires$,
            this.pricingPlan$,
            this.userClaims$,
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: ([organization, industries, entities, questionnaire, pricingplan, claims]) => {
                    if (organization && organization.uid && entities && industries) {
                        this.organization = organization;
                        this.entities = entities.filter((entity) => !entity.deleted);
                        this.industries = industries;
                        this.activeQuestionnaire = questionnaire;
                        this.pricingPlan = pricingplan;
                        this.isLoading = false;
                        this.isOrgOwner = claims.tasks.some(
                            (task) => task.name === 'orguser' && task.organization_id === claims.organization?.uid,
                        );
                        this.canAddEntities =
                            this.entities.length < this.pricingPlan.licenseDoc.max_entities && this.isOrgOwner;

                        this.questionnaireService.getCurrencies(organization.uid).subscribe((currencies) => {
                            this.currencies = currencies;
                        });
                    }
                },
            });

        this.countries = buildCountryList();
        this.countryCitiesLUT = buildCountryCitiesLUT();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public addEditEntity(entity: OrganizationEntityDto | null = null): void {
        this.selectedEntity = entity;
        this.addingEditingMode = true;
    }

    public getIndustryDisplay(industryId: string): string {
        const res = this.industries.find((x) => x.industrySectorId === industryId);

        return res
            ? this.translateService.instant(`components.settingsForm.industry.${res.industrySectorLabel}`)
            : 'N/A';
    }

    onCloseEditEntity(event?) {
        this.addingEditingMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.status) {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                    if (event.hasFteDecreased) {
                        this.toastService.showWarning({
                            details: 'components.settingsForm.entitySection.toasts.fteDecrease',
                            life: 8000,
                        });
                    }
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
            });
        }
    }

    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }

    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }
}
